import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, IconButton } from '@c2fo/liquidity';
import { DownloadIcon } from '@c2fo/liquidity/icons';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';

export type InvoiceDownloadMenuScope = 'all' | 'filtered';

export interface InvoiceDownloadMenuProps {
  /** Disable trigger and menu items (e.g. empty stats). */
  disabled?: boolean;
  /**
   * Eligible / ineligible / adjustments (SEA): all vs urlState-filtered.
   */
  onDownload: (scope: InvoiceDownloadMenuScope) => void | Promise<void>;
}

/** Same look as a disabled download trigger — use while stats are loading to avoid flicker. */
export const InvoiceDownloadMenuLoading = () => {
  const { t } = useTranslation();

  return <IconButton disabled icon={DownloadIcon} name={t('core.downloadInvoices')} variant="ancillary" />;
};

/**
 * Filter-bar download control: IconButton (download icon) → all / filtered menu.
 * Shared toolbars (*ToolbarActions) own SEA download; hosts pass thin analytics callbacks.
 */
const InvoiceDownloadMenu = ({ disabled = false, onDownload }: InvoiceDownloadMenuProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const menuDisabled = disabled || isDownloading;

  const handleSelect = (scope: InvoiceDownloadMenuScope) => {
    if (menuDisabled) {
      return;
    }

    void (async () => {
      setIsDownloading(true);
      try {
        await onDownload(scope);
      } finally {
        setIsDownloading(false);
      }
    })();
  };

  return (
    <IntercomDataTarget target="invoiceDownloadMenu">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            disabled={menuDisabled}
            icon={DownloadIcon}
            name={t('core.downloadInvoices')}
            variant="ancillary"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
          <DropdownMenuItem
            disabled={menuDisabled}
            onSelect={() => {
              handleSelect('all');
            }}
          >
            {t('core.downloadAllInvoices')}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={menuDisabled}
            onSelect={() => {
              handleSelect('filtered');
            }}
          >
            {t('core.downloadFilteredInvoices')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </IntercomDataTarget>
  );
};

export default InvoiceDownloadMenu;
